import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { BuildingOfficeIcon, UserGroupIcon, ClockIcon, ArrowPathIcon, UserIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

/** Format date as yyyy-mm-dd hh:mm:ss (local time) */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function SkeletonCard() {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 animate-pulse">
      <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
      <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
    </div>
  );
}

function SkeletonTimelineRow() {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-gray-200 dark:border-gray-700 last:border-0 animate-pulse">
      <div className="h-4 flex-1 max-w-[180px] bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-4 flex-1 max-w-[140px] bg-gray-200 dark:bg-gray-700 rounded" />
      <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
      <div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
      <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded shrink-0" />
    </div>
  );
}

type UsagesView = 'all' | 'companies' | 'personal';

const AdministrationUsages: React.FC = () => {
  const { t } = useTranslation('usages');
  const { user } = useAuth();
  const { companyId: urlCompanyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  usePageTitle(t('title'), t('description'));

  const PERIOD_OPTIONS = [
    { value: '1h', label: t('periodLastHour') },
    { value: '24h', label: t('periodLast24h') },
    { value: '7d', label: t('periodLast7d') },
    { value: '30d', label: t('periodLast30d') },
  ] as const;

  const isSysadmin = user?.role === 'sysadmin';
  const isAdmin = user?.role === 'admin' || user?.role === 'sysadmin';

  // URL-driven view for sysadmin: /usages = all, /usages/companies = companies, /usages/companies/:id = company, /usages/personal = personal
  const path = location.pathname;
  const view: UsagesView = isSysadmin
    ? path === '/administration/usages/personal'
      ? 'personal'
      : path.startsWith('/administration/usages/clients')
        ? 'companies'
        : 'all'
    : 'all';

  // For sysadmin: effective company is from URL when on companies/:companyId. For company admin: always their company.
  const effectiveCompanyId = isSysadmin
    ? (view === 'companies' && urlCompanyId ? urlCompanyId : null)
    : (user?.company_id ?? null);

  const [timelinePeriod, setTimelinePeriod] = useState<string>('24h');
  const [licenseStatusFilter, setLicenseStatusFilter] = useState<string>('active');
  const [licenseTypeFilter, setLicenseTypeFilter] = useState<string>('all');
  const [licenseSortBy, setLicenseSortBy] = useState<'created_at' | 'revoked_at'>('created_at');
  const [licenseSortOrder, setLicenseSortOrder] = useState<'asc' | 'desc'>('desc');

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiService.getCompanies(),
    enabled: isSysadmin,
  });

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['sysadmin-usages-overview'],
    queryFn: () => apiService.getSysadminUsagesOverview(),
    enabled: isSysadmin && view === 'all',
  });

  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ['sysadmin-usages-timeline', timelinePeriod],
    queryFn: () => apiService.getSysadminUsagesTimeline(timelinePeriod),
    enabled: isSysadmin && view === 'all',
  });

  const { data: usages, isLoading } = useQuery({
    queryKey: ['company-usages', effectiveCompanyId, licenseStatusFilter, licenseTypeFilter],
    queryFn: () => apiService.getCompanyUsages(effectiveCompanyId!, { status: licenseStatusFilter, type: licenseTypeFilter }),
    enabled: !!effectiveCompanyId && !!user,
  });

  const { data: personalUsages, isLoading: personalLoading } = useQuery({
    queryKey: ['sysadmin-usages-personal'],
    queryFn: () => apiService.getSysadminUsagesPersonal(),
    enabled: isSysadmin && view === 'personal',
  });

  const queryClient = useQueryClient();
  const syncUsagesMutation = useMutation({
    mutationFn: () => apiService.syncSysadminUsages(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sysadmin-usages-overview'] });
      queryClient.invalidateQueries({ queryKey: ['sysadmin-usages-timeline'] });
      queryClient.invalidateQueries({ queryKey: ['company-usages'] });
      queryClient.invalidateQueries({ queryKey: ['sysadmin-usages-personal'] });
    },
  });

  const goToView = (v: UsagesView, companyId?: string) => {
    if (v === 'all') navigate('/administration/usages');
    else if (v === 'personal') navigate('/administration/usages/personal');
    else if (v === 'companies') navigate(companyId ? `/administration/usages/clients/${companyId}` : '/administration/usages/clients');
  };

  type LicenseRow = { id: string; user_id: string; user_email?: string; type: string; status: string; created_at: string; revoked_at?: string };
  const licenses = useMemo(() => {
    const licensesRaw = usages?.licenses ?? [];
    const list = [...licensesRaw] as LicenseRow[];
    list.sort((a, b) => {
      const key = licenseSortBy;
      const rev = licenseSortOrder === 'desc' ? 1 : -1;
      if (key === 'created_at') {
        const ta = new Date(a.created_at).getTime();
        const tb = new Date(b.created_at).getTime();
        return rev * (ta - tb);
      }
      const ta = a.revoked_at ? new Date(a.revoked_at).getTime() : 0;
      const tb = b.revoked_at ? new Date(b.revoked_at).getTime() : 0;
      if (ta === 0 && tb === 0) return 0;
      if (ta === 0) return licenseSortOrder === 'desc' ? 1 : -1;
      if (tb === 0) return licenseSortOrder === 'desc' ? -1 : 1;
      return rev * (ta - tb);
    });
    return list;
  }, [usages?.licenses, licenseSortBy, licenseSortOrder]);

  const handleSort = (column: 'created_at' | 'revoked_at') => {
    if (licenseSortBy === column) {
      setLicenseSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setLicenseSortBy(column);
      setLicenseSortOrder('desc');
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setLicenseStatusFilter(value);
    if (value === 'revoked') {
      setLicenseSortBy('revoked_at');
      setLicenseSortOrder('desc');
    } else {
      setLicenseSortBy('created_at');
      setLicenseSortOrder('desc');
    }
  };

  if (!user) return null;
  if (!isAdmin) {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">{t('noPermission')}</p>
      </div>
    );
  }

  const usageRows = usages?.usage_rows ?? [];
  const personalRows = personalUsages?.usage_rows ?? [];

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      {isSysadmin && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-1">
            <button
              type="button"
              onClick={() => goToView('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                view === 'all'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t('filterAll')}
            </button>
            <button
              type="button"
              onClick={() => goToView('companies')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                view === 'companies'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <BuildingOfficeIcon className="w-4 h-4" />
              {t('filterCompanies')}
            </button>
            <button
              type="button"
              onClick={() => goToView('personal')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                view === 'personal'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <UserIcon className="w-4 h-4" />
              {t('filterPersonal')}
            </button>
          </div>
          {view === 'companies' && companies && companies.length > 0 && (
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="w-5 h-5 text-gray-500 shrink-0" />
              <select
                value={urlCompanyId ?? ''}
                onChange={(e) => goToView('companies', e.target.value || undefined)}
                className="min-w-[200px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
              >
                <option value="">{t('selectCompany')}</option>
                {companies.map((c: { id: string; name: string }) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {view === 'all' && isSysadmin && (
        <>
          <div className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('platformOverview')}</h2>
              <button
                type="button"
                onClick={() => syncUsagesMutation.mutate()}
                disabled={syncUsagesMutation.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowPathIcon className={`w-4 h-4 ${syncUsagesMutation.isPending ? 'animate-spin' : ''}`} />
                {syncUsagesMutation.isPending ? t('syncing') : t('syncUsages')}
              </button>
            </div>
            {overviewLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : overview ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('companies')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{overview.total_companies}</p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('fullLicensesUsed')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{overview.full_licenses_used}</p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('readerLicensesUsed')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">{overview.reader_licenses_used}</p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <ClockIcon className="w-5 h-5 text-indigo-500" />
                {t('usageTimeline')}
              </h2>
              <div className="flex items-center gap-3">
                {timeline?.last_synced_at && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('lastSyncedAt')}: {formatDateTime(timeline.last_synced_at)}
                  </span>
                )}
                <select
                  value={timelinePeriod}
                  onChange={(e) => setTimelinePeriod(e.target.value)}
                  className="min-w-[160px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                >
                  {PERIOD_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-x-auto">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 grid grid-cols-10 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span className="col-span-4">{t('user')}</span>
                <span className="col-span-4">{t('company')}</span>
                <span className="col-span-1">{t('devices')}</span>
                <span className="col-span-1">{t('folders')}</span>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {timelineLoading ? (
                  Array.from({ length: 8 }).map((_, i) => <SkeletonTimelineRow key={i} />)
                ) : timeline?.entries?.length ? (
                  timeline.entries.map((entry: { user_id: string; user_email: string; company_id: string; company_name: string; devices_count: number; folders_count: number }, idx: number) => (
                    <div
                      key={entry.user_id ? `${entry.user_id}-${idx}` : `row-${idx}`}
                      className="grid grid-cols-10 gap-2 px-4 py-3 text-sm items-center hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <span className="col-span-4 truncate">
                        {entry.user_id ? (
                          <Link to={`/users/${entry.user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium" title={entry.user_email}>
                            {entry.user_email}
                          </Link>
                        ) : (
                          <span className="text-gray-900 dark:text-white">{entry.user_email}</span>
                        )}
                      </span>
                      <span className="col-span-4 truncate">
                        {entry.company_id ? (
                          <Link to={`/clients/${entry.company_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium" title={entry.company_name}>
                            {entry.company_name}
                          </Link>
                        ) : (
                          <span className="text-gray-700 dark:text-gray-300">{entry.company_name}</span>
                        )}
                      </span>
                      <span className="col-span-1 text-gray-700 dark:text-gray-300">{entry.devices_count}</span>
                      <span className="col-span-1 text-gray-700 dark:text-gray-300">{entry.folders_count}</span>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('noUsageUpdates')}
                  </div>
                )}
              </div>
            </div>
          </div>

         
        </>
      )}

      {view === 'companies' && isSysadmin && !urlCompanyId && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-8 text-center">
          <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
          <p className="text-gray-600 dark:text-gray-400">{t('selectCompanyToSeeUsage')}</p>
        </div>
      )}

      {view === 'personal' && isSysadmin && (
        <>
          {personalLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-indigo-500" />
                <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('usagePersonal')}</h2>
              </div>
              {personalRows.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('noUsageRows')}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('userEmail')}</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('devices')}</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('folders')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {personalRows.map((r: { user_id: string; user_email: string; devices_count: number; folders_count: number }) => (
                        <tr key={r.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 text-sm">
                            <Link to={`/users/${r.user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">{r.user_email}</Link>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.devices_count}</td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.folders_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!effectiveCompanyId && !isSysadmin && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 text-center">
          <p className="text-gray-600 dark:text-gray-400">{t('notAssignedToCompany')}</p>
        </div>
      )}

      {effectiveCompanyId && (
        <>
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['company-usages', effectiveCompanyId, licenseStatusFilter, licenseTypeFilter] });
              }}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              {t('syncUsages')}
            </button>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
            </div>
          ) : usages ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('fullLicenses')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {usages.full_licenses_used} / {usages.full_licenses_max}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('readerLicenses')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {usages.reader_licenses_used} / {usages.reader_licenses_max}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{t('auditorLicenses')}</p>
                  <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                    {usages.auditor_licenses_used ?? 0} / {usages.auditor_licenses_max != null ? usages.auditor_licenses_max : '∞'}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm mb-8">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
                  <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('licensesSection')}</h2>
                  <div className="flex flex-wrap items-center gap-3 min-w-0">
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{t('licenseStatus')}</span>
                    <select
                      value={licenseStatusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm min-w-[9rem]"
                    >
                      <option value="all">{t('filterAll')}</option>
                      <option value="active">{t('licenseStatusActive')}</option>
                      <option value="revoked">{t('licenseStatusRevoked')}</option>
                    </select>
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{t('licenseType')}</span>
                    <select
                      value={licenseTypeFilter}
                      onChange={(e) => setLicenseTypeFilter(e.target.value)}
                      className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm min-w-[8rem]"
                    >
                      <option value="all">{t('filterAll')}</option>
                      <option value="full">{t('licenseTypeFull')}</option>
                      <option value="reader">{t('licenseTypeReader')}</option>
                      <option value="auditor">{t('licenseTypeAuditor')}</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('user')}</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('licenseType')}</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('status')}</th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          <button type="button" onClick={() => handleSort('created_at')} className="inline-flex items-center gap-0.5 hover:text-gray-700 dark:hover:text-gray-300">
                            {t('licenseCreated')}
                            {licenseSortBy === 'created_at' ? (licenseSortOrder === 'desc' ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />) : <span className="w-4 h-4 opacity-30"><ChevronDownIcon className="w-4 h-4" /></span>}
                          </button>
                        </th>
                        <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          <button type="button" onClick={() => handleSort('revoked_at')} className="inline-flex items-center gap-0.5 hover:text-gray-700 dark:hover:text-gray-300">
                            {t('licenseRevokedAt')}
                            {licenseSortBy === 'revoked_at' ? (licenseSortOrder === 'desc' ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />) : <span className="w-4 h-4 opacity-30"><ChevronDownIcon className="w-4 h-4" /></span>}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {licenses.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            {t('noLicenses')}
                          </td>
                        </tr>
                      ) : (
                        licenses.map((lic: { id: string; user_id: string; user_email?: string; type: string; status: string; created_at: string; revoked_at?: string }) => (
                          <tr key={lic.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-2 text-sm">
                              <Link to={`/users/${lic.user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                                {lic.user_email ?? lic.user_id}
                              </Link>
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                              {lic.type === 'auditor' ? t('licenseTypeAuditor') : lic.type === 'full' ? t('licenseTypeFull') : lic.type === 'reader' ? t('licenseTypeReader') : lic.type}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{lic.status}</td>
                            <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-300">{formatDateTime(lic.created_at)}</td>
                            <td className="px-4 py-2 text-sm font-mono text-gray-600 dark:text-gray-300">{lic.revoked_at ? formatDateTime(lic.revoked_at) : '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <UserGroupIcon className="w-5 h-5 text-indigo-500" />
                  <h2 className="text-sm font-medium text-gray-900 dark:text-white">{t('usageDevicesFoldersPerUser')}</h2>
                </div>
                {usageRows.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('noUsageRows')}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-800/50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('userEmail')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('devices')}
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('folders')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {usageRows.map((r: { user_id: string; user_email: string; devices_count: number; folders_count: number }) => (
                          <tr key={r.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-4 py-3 text-sm">
                              <Link to={`/users/${r.user_id}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
                                {r.user_email}
                              </Link>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.devices_count}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.folders_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default AdministrationUsages;
